/**
 * DistanceSuggester — infers race distance (km) from a race name string.
 *
 * Uses a dictionary of well-known races and regex pattern matching as fallback.
 * Pure service, no Firebase dependencies.
 */
export class DistanceSuggester {
  /** Dictionary of popular races → distance in km. Keys are lowercase. */
  static KNOWN_RACES = {
    // World Marathon Majors
    'boston marathon': 42.195,
    'new york city marathon': 42.195,
    'nyc marathon': 42.195,
    'chicago marathon': 42.195,
    'berlin marathon': 42.195,
    'london marathon': 42.195,
    'tokyo marathon': 42.195,

    // Indian Marathons & Races (IMPORTANT — this is an Indian product)
    'tata mumbai marathon': 42.195,
    'mumbai marathon': 42.195,
    'tata mumbai half marathon': 21.0975,
    'tcs world 10k': 10,
    'tcs world 10k bangalore': 10,
    'tcsw10k': 10,
    'tcs 10k': 10,
    'airtel delhi half marathon': 21.0975,
    'delhi half marathon': 21.0975,
    'adhm': 21.0975,
    'vedanta delhi half marathon': 21.0975,
    'hyderabad marathon': 42.195,
    'bangalore marathon': 42.195,
    'chennai marathon': 42.195,
    'goa river marathon': 42.195,
    'pune marathon': 42.195,
    'kolkata marathon': 42.195,
    'kaveri trail marathon': 42.195,
    'malnad ultra': 100,
    'stadium run': 10,
    'pinkathon': 10,
    'satara hill half marathon': 21.0975,
    'ladakh marathon': 42.195,
    'khardung la challenge': 72,
    'comrade of india ultra': 50,
    'nilgiris ultra': 100,
    'kodai ultra': 60,
    'ultra booster bhopal': 50,
    'wipro chennai marathon': 42.195,
    'namma run bangalore': 10,
    'procam slam': 42.195,
    'idbi federal life insurance kolkata marathon': 42.195,
    'great hyderabad adventure club marathon': 42.195,
    'vasai virar mayors marathon': 21.0975,
    'thane mayor marathon': 21.0975,
    'ironman goa': 226, // full triathlon distance (swim+bike+run)

    // Indian Half Marathons
    'mumbai half marathon': 21.0975,
    'bangalore half marathon': 21.0975,
    'hyderabad half marathon': 21.0975,
    'chennai half marathon': 21.0975,
    'pune half marathon': 21.0975,
    'goa half marathon': 21.0975,

    // Ultras (International)
    'ultra trail du mont blanc': 171,
    'utmb': 171,
    'western states': 161,
    'comrades marathon': 89,
    'badwater': 217,

    // Other popular international races
    'paris marathon': 42.195,
    'amsterdam marathon': 42.195,
    'dubai marathon': 42.195,
    'hong kong marathon': 42.195,
    'sydney marathon': 42.195,
    'melbourne marathon': 42.195,
    'singapore marathon': 42.195,
  };

  /**
   * Pattern matching rules, ordered so more specific patterns come first.
   * "half marathon" must be tested before bare "marathon".
   */
  static PATTERNS = [
    { pattern: /\b5\s*k\b/i, distance: 5 },
    { pattern: /\b10\s*k\b/i, distance: 10 },
    { pattern: /\bhalf\s*marathon\b/i, distance: 21.0975 },
    { pattern: /\bmarathon\b/i, distance: 42.195 }, // must come after "half marathon"
    { pattern: /\b50\s*k\b/i, distance: 50 },
    { pattern: /\b100\s*k\b/i, distance: 100 },
    { pattern: /\b50\s*mi/i, distance: 80.467 },
    { pattern: /\b100\s*mi/i, distance: 160.934 },
  ];

  /**
   * Suggest a distance in km for the given race name.
   *
   * 1. Exact match against the known-races dictionary (case-insensitive, trimmed).
   * 2. Regex pattern matching.
   * 3. Returns null if no match.
   *
   * @param {string} raceName
   * @returns {number|null} distance in km, or null
   */
  static suggest(raceName) {
    if (!raceName || typeof raceName !== 'string') return null;

    const normalized = raceName.trim().toLowerCase();
    if (normalized === '') return null;

    // 1. Dictionary lookup
    if (DistanceSuggester.KNOWN_RACES[normalized] != null) {
      return DistanceSuggester.KNOWN_RACES[normalized];
    }

    // 2. Pattern matching
    for (const { pattern, distance } of DistanceSuggester.PATTERNS) {
      if (pattern.test(raceName)) {
        return distance;
      }
    }

    return null;
  }
}
